import { resolveWebStorage } from './storage.helpers';

/** Prefix `oidc-client-ts` puts on every entry it writes to web storage. */
const OIDC_STORAGE_PREFIX = 'oidc.';

/**
 * Age beyond which an abandoned protocol-state entry is safe to drop. Matches
 * the library's own `staleStateAgeInSeconds` default, so the sweep can never be
 * more aggressive than the cleanup `oidc-client-ts` already performs itself.
 */
export const DEFAULT_LEGACY_STATE_MAX_AGE_SECONDS = 900;

/** The two fields every `State` entry carries. `created` is in epoch seconds. */
type StoredState = { id: string; created: number };

export type PurgeLegacyOidcStateOptions = {
  /** Defaults to `window.localStorage`. Injected for tests. */
  storage?: Storage;
  maxAgeSeconds?: number;
  /** Epoch milliseconds. Injected for tests. */
  nowMs?: number;
};

/**
 * Removes abandoned OIDC protocol state left behind in `localStorage`.
 *
 * `oidc-client-ts` defaults `stateStore` to `localStorage` while defaulting
 * `userStore` to `sessionStorage`. One entry — holding the flow's PKCE
 * `code_verifier` — is written per authorize request and removed only when the
 * matching callback is processed, so every abandoned flow (notably a silent
 * renew that times out) orphans one forever. We now pin state to
 * `sessionStorage`, which means the library's own `clearStaleState()` no longer
 * looks at `localStorage` and cannot clear the backlog. This does. See FD-3069.
 *
 * Two guards keep the sweep off entries that are not ours to delete — hr-fe and
 * the other apps share this origin:
 * - the value must parse as protocol state (a stored `User` has neither `id`
 *   nor `created`, so a live session is never touched);
 * - it must be older than `maxAgeSeconds`, so a flow in progress in another tab
 *   or another app survives.
 *
 * @returns how many entries were removed.
 */
export function purgeLegacyOidcState(options: PurgeLegacyOidcStateOptions = {}): number {
  const storage = options.storage ?? resolveWebStorage('local');
  if (!storage) return 0;

  const nowSeconds = (options.nowMs ?? Date.now()) / 1000;
  const cutoff =
    nowSeconds - (options.maxAgeSeconds ?? DEFAULT_LEGACY_STATE_MAX_AGE_SECONDS);

  try {
    return removeStaleStateEntries(storage, cutoff);
  } catch {
    // Storage can refuse wholesale (disabled by policy, private mode, quota).
    // Housekeeping must never be the reason an app fails to boot.
    return 0;
  }
}

function removeStaleStateEntries(storage: Storage, cutoff: number): number {
  const stale = readOidcKeys(storage).filter((key) =>
    isStaleState(storage.getItem(key), cutoff),
  );
  stale.forEach((key) => storage.removeItem(key));
  return stale.length;
}

/** Collected before removing anything — mutating a store mid-enumeration skips keys. */
function readOidcKeys(storage: Storage): string[] {
  const keys: string[] = [];
  for (let index = 0; index < storage.length; index++) {
    const key = storage.key(index);
    if (key?.startsWith(OIDC_STORAGE_PREFIX)) keys.push(key);
  }
  return keys;
}

function isStaleState(raw: string | null, cutoff: number): boolean {
  const state = parseState(raw);
  return state !== null && state.created <= cutoff;
}

function parseState(raw: string | null): StoredState | null {
  if (!raw) return null;
  try {
    const value: unknown = JSON.parse(raw);
    return isStoredState(value) ? value : null;
  } catch {
    return null;
  }
}

function isStoredState(value: unknown): value is StoredState {
  if (typeof value !== 'object' || value === null) return false;
  const { id, created } = value as Partial<StoredState>;
  return typeof id === 'string' && typeof created === 'number';
}
